/**
 * AI 모의투자 화면의 순수 판정. (18단계)
 *
 * 🔴 왜 컴포넌트에서 떼어냈나
 *   여기 있는 판단들은 눈으로 확인하기 어렵다. 표본이 적을 때의 문구,
 *   조건이 깨진 채로 산 체결의 표시, 빈 화면의 이유 —
 *   전부 특정 상태에서만 나타나고 그 상태를 손으로 만들기 번거롭다.
 *
 *   watchlistGroup.ts 와 같은 이유다.
 */

import type {
  PaperClosedTrade,
  PaperHolding,
  PaperOverview,
  PaperProfileStats,
  PaperRoundClosing,
} from './paperTypes';

/**
 * 승률을 참고용으로만 보라고 알릴 기준.
 *
 * 지난 추천 화면과 같은 값이다. 1승 0패의 100% 와 30승 0패의 100% 를
 * 같게 보여주면 안 된다. (JournalPage 의 SMALL_SAMPLE)
 */
export const SMALL_SAMPLE = 10;

/**
 * 모의투자 화면의 탭.
 *
 * 🔴 BAND_AGGRESSIVE · BAND_CHALLENGER 는 백엔드 키와 글자까지 같아야 한다 (V119 · 2026-09-18)
 *   정본은 백엔드 BandVariant.screenKey() 다.
 *   비교 탭이 이 문자열로 곡선을 가르고, 회차 초기화 결과도 이 키로 돌아온다.
 */
export type PaperTab =
  | 'SAFE'
  | 'MODERATE'
  | 'AGGRESSIVE'
  | 'BAND'
  | 'BAND_AGGRESSIVE'
  | 'BAND_CHALLENGER'
  // 🔁 국내 스윙 — 4회차부터(2026-10-10 · 141번 12.6-5). 백엔드 SwingAccountKey.screenKey() 와 같은 글자
  | 'SWING_A'
  | 'SWING_C'
  // 🇺🇸 미국 저점매수 — 미국 화면의 "?" 도움말만 쓴다(국내 탭 줄에는 없다 · 백엔드 BandVariant.US_P20.screenKey())
  | 'BAND_US_P20'
  // 🇺🇸 미국 스윙 — 미국 화면의 탭 · "?" 만(2026-10-10 · 백엔드 SwingAccountKey.SWING_US_A)
  | 'SWING_US_A'
  // 🇺🇸 미국 추천 R3 — 미국 화면의 탭 · "?" 만(2026-10-11 E 화면 · 백엔드 RecommendationProfile.US_SAFE_R3)
  | 'US_SAFE_R3'
  | 'COMPARISON'
  // 계좌가 아니라 지난 회차를 읽는 탭이다 (2026-09-03, docs/impl/107)
  | 'ROUNDS';

/**
 * 화면이 비어 있는 이유. 비어 있지 않으면 null 이다.
 *
 * 🔴 "왜 비어 있는가" 를 적지 않으면 배치가 죽은 줄 안다
 *   지난 추천 화면에서 같은 것을 겪었다. 처음 3주가 반드시 빈 화면인데
 *   그 이유가 화면에 없어서 매번 로그를 봤다.
 */
export function emptyReason(overview: PaperOverview): string | null {
  if (overview.holdings.length > 0 || overview.closed.length > 0) {
    return null;
  }
  // 🇺🇸 미국 추천(2026-10-11 E 화면) — 시각이 다르다: 고르기 오전 10:45 · 매매 밤 미국 정규장 · 하루 평가 오전 11:00
  if (overview.account.currency === 'USD') {
    if (overview.account.waiting) {
      return '4회차가 열리면 고르기 시작합니다. 지금은 계좌만 준비돼 있습니다.';
    }
    if (overview.curve.length === 0) {
      return '아직 첫 하루 평가가 없습니다. 미국 장이 끝난 다음 날 오전 11:00에 한 점씩 남깁니다.';
    }
    return '아직 매수한 종목이 없습니다. 매일 오전 10:45에 그날 밤 미국 장의 추천을 고르고, '
      + '한국 시각 밤 미국 정규장 동안 매분 지켜보다가 정한 가격대에 오면 삽니다.';
  }
  if (overview.curve.length === 0) {
    return '아직 첫 스냅샷이 찍히지 않았습니다. 평일 20:50 배치가 하루 한 점씩 남깁니다.';
  }
  // 2026-08-23 부터 장중 매분 감시가 눌린 자리를 기다렸다 산다 (79번 노트).
  // 체크포인트(09:05 등)는 안전망으로 남아 있지만 "첫 기회" 는 개장 직후다.
  // NXT 에서도 거래되는 추천 종목은 장전 08:00~08:50 부터 산다 (trading.yml paper.pre-market)
  // ✏️ 2026-09-29 I-57 — 3회차부터라고 적었지만 저장 누락으로 3회차에는 한 번도 안 샀다. 사용자 결정으로 4회차부터(from-round: 4)
  return '아직 매수한 종목이 없습니다. 추천이 나온 다음 영업일 개장(09:00)부터 매분 지켜보다가, '
    + '눌린 자리에 오면 삽니다. 4회차부터는 NXT에서도 거래되는 종목을 장전(08:00~08:50)부터 봅니다.';
}

/**
 * 이 체결이 진입 조건이 깨진 채로 매수됐는가.
 *
 * 🔴 null 은 false 와 다르다
 *   null 은 MA20 이 없어 확인하지 못한 것이고, false 는 조건이 깨진 것이다.
 *   확인 못 한 것에 경고를 붙일 근거가 없다.
 */
export function enteredAgainstCondition(
  trade: Pick<PaperHolding | PaperClosedTrade, 'entryValid'>,
): boolean {
  return trade.entryValid === false;
}

/**
 * 승률 옆에 붙일 모수 문구.
 *
 * 청산이 0건이면 승률 자체가 없다. "0%" 로 두면 전패로 읽힌다.
 */
export function winRateLabel(stats: Pick<PaperProfileStats, 'winRate' | 'closedCount' | 'winCount'>): {
  value: string;
  sample: string;
  small: boolean;
} {
  if (stats.closedCount === 0) {
    return { value: '—', sample: '청산 0건', small: true };
  }
  return {
    value: `${stats.winRate?.toFixed(1) ?? '—'}%`,
    sample: `${stats.winCount}승 ${stats.closedCount - stats.winCount}패 · 모수 ${stats.closedCount}건`,
    small: stats.closedCount < SMALL_SAMPLE,
  };
}

/**
 * 누적 수익률이 높은 순으로 줄 세운다. (2026-08-30)
 *
 * 🔴 금액이 아니라 수익률로 세운다
 *   계좌마다 시드가 다를 수 있다. 금액으로 세우면 시드가 큰 계좌가
 *   같은 성적에도 위로 온다.
 *
 * 🔴 원본을 바꾸지 않는다 - TanStack Query 캐시 배열을 제자리 정렬하면
 *   다음 렌더의 비교가 어긋난다.
 *
 * 수익률이 없는 계좌(스냅샷 전)는 맨 아래로 보낸다 - 0% 로 치면
 * 손익 0인 계좌와 구분되지 않는다.
 */
export function rankByReturn(profiles: PaperProfileStats[]): PaperProfileStats[] {
  return [...profiles].sort((a, b) => {
    const av = a.totalReturnPercent;
    const bv = b.totalReturnPercent;
    if (av === null && bv === null) {
      return 0;
    }
    if (av === null) {
      return 1;
    }
    if (bv === null) {
      return -1;
    }
    return bv - av;
  });
}

/**
 * 마감 성적 한 줄 + 화면 이름(계좌 통계에서 붙인다).
 * 🔴 청산 건수는 붙이지 않는다 — 계좌 통계의 건수도 마감 뒤 공백 동안의 매도로 늘 수 있어 성적표와 어긋난다
 */
export interface ClosingRow extends PaperRoundClosing {
  label: string;
}

/**
 * 끝난 회차의 순위 — 마감 성적표(round.closing)로 줄 세운다. (2026-09-29 L4 · 사용자 결정 "마감 성적으로 고정")
 *
 * 🔴 왜 계좌 통계로 세우지 않나
 *   4회차부터 회차가 끝나도 보유를 팔지 않는다(U-31). 계좌 통계의 누적 수익률은 마감 뒤에도 매일 움직여
 *   "N회차 성적 순위" 가 회차 이력의 성적표와 어긋난다. 성적표는 마감일 종가로 한 번 적고 안 바뀐다.
 *
 * 이름은 계좌 통계(profiles)에서 키로 찾는다. 성적표에만 있는 계좌(그 뒤 없어진 계좌)는 키를 그대로 이름으로 쓴다.
 * 원본 배열을 바꾸지 않는다(rankByReturn 과 같은 이유).
 */
export function rankClosing(closing: PaperRoundClosing[], profiles: PaperProfileStats[]): ClosingRow[] {
  const byKey = new Map<string, PaperProfileStats>(profiles.map((p) => [p.profile, p]));
  return closing
    .map((c) => {
      const p = byKey.get(c.account);
      return { ...c, label: p?.profileLabel ?? c.account };
    })
    .sort((a, b) => b.returnPercent - a.returnPercent);
}

/**
 * 계좌를 화면에 놓을 순서. 위험이 커지는 방향이다.
 *
 * 🔴 알파벳순으로 두면 AGGRESSIVE 가 먼저 와서 하이리스크가 왼쪽에 놓인다
 *   기본값이 위험한 쪽으로 읽힌다. 안전을 먼저 둔다.
 *
 * 저점매수(BAND)는 맨 뒤다. 앞의 셋은 같은 종목을 사고 매도만 다른
 * 한 계열이라 붙여 두고, 종목 고르는 방식부터 다른 것을 그 뒤에 놓는다.
 * (탭 순서와도 같다 - PaperPage.TABS)
 *
 * 공격 버전은 저점매수 바로 뒤다 - 둘은 매수 기준가 하나만 다른 형제라
 * 떨어뜨려 놓으면 표에서 나란히 비교되지 않는다. (V119)
 * 챌린저는 그 뒤다 - 리스크와 견주는 계좌라 바로 옆에 있어야 한다 (2026-09-18)
 */
/**
 * 쉬는 추천 프로필의 탭을 뺀다. (2026-10-10 사용자 결정 · 141번 10.6 — 분할익절 · 하이리스크 잠정 중단)
 *
 * AI 모의투자 계좌 탭(PaperPage)과 추천 목록 프로필 탭(ProfileTabs)이 같이 쓴다.
 * key 가 undefined 인 탭("전체")과 쉬는 목록에 없는 탭(저점매수 · 비교 · 회차 이력)은 그대로 둔다. 순서는 바꾸지 않는다.
 *
 * @param resting 서버가 준 지금 쉬는 프로필(GET /trading/paper/resting). 비어 있으면 그대로 돌려준다
 */
export function withoutResting<T extends { key: string | undefined }>(
  tabs: readonly T[],
  resting: readonly string[],
): T[] {
  return tabs.filter((tab) => tab.key === undefined || !resting.includes(tab.key));
}

/**
 * 탭 목록의 key 뒤에 extra 를 끼운다 — 국내 스윙 탭을 저점매수(챌린저) 뒤에 놓는다 (2026-10-10).
 * key 가 없으면(챌린저 탭이 빠진 목록 등) 비교 탭 앞에, 그것도 없으면 맨 끝에 둔다 — 탭이 사라지지 않게.
 */
export function insertAfter<T extends { key: string }>(tabs: readonly T[], key: string, extra: readonly T[]): T[] {
  if (extra.length === 0) {
    return [...tabs];
  }
  let at = tabs.findIndex((tab) => tab.key === key) + 1;
  if (at === 0) {
    const comparison = tabs.findIndex((tab) => tab.key === 'COMPARISON');
    at = comparison >= 0 ? comparison : tabs.length;
  }
  return [...tabs.slice(0, at), ...extra, ...tabs.slice(at)];
}

export function orderProfiles(profiles: PaperProfileStats[]): PaperProfileStats[] {
  const order: Record<string, number> = {
    SAFE: 0, MODERATE: 1, AGGRESSIVE: 2, BAND: 3, BAND_AGGRESSIVE: 4, BAND_CHALLENGER: 5,
    // 🔁 국내 스윙 — 4회차부터 비교 탭에 들어온다(저점매수 뒤 · 2026-10-10)
    SWING_A: 6, SWING_C: 7,
  };
  return [...profiles].sort((a, b) => (order[a.profile] ?? 9) - (order[b.profile] ?? 9));
}

/**
 * 보유가 만료까지 며칠 남았나. 만료일을 모르면 null 이다.
 *
 * 음수면 이미 지난 것이다 — 그날 마지막 장중 감시(19:50~20:00, PaperIntradayJob)에서 청산된다.
 * (2026-09-20 까지는 CLOSE 체크포인트 15:10 이었다 · 9/28 I-78 2차)
 */
export function daysToExpiry(holding: PaperHolding, today: string): number | null {
  if (!holding.evaluateOn) {
    return null;
  }
  const expiry = Date.parse(holding.evaluateOn);
  const now = Date.parse(today);
  if (Number.isNaN(expiry) || Number.isNaN(now)) {
    return null;
  }
  return Math.round((expiry - now) / (24 * 60 * 60 * 1000));
}

/**
 * 장전(NXT 프리마켓) 매수인가. 매수 시각이 08:00~08:59 면 참이다. (2026-09-19 I-3)
 *
 * 장전 매수도 장중 매수처럼 체크포인트가 null 이라, 시각으로만 가를 수 있다.
 * buyAt 은 "2026-09-21T08:10:30" 꼴(KST, 백엔드 LocalDateTime 직렬화)이다.
 * null 이면(2026-09-10 이전 건) 장전 매수가 있던 때가 아니므로 거짓이다.
 */
export function isPreMarketBuy(buyAt: string | null): boolean {
  if (!buyAt || buyAt.length < 16) {
    return false;
  }
  const hhmm = buyAt.slice(11, 16);
  return hhmm >= '08:00' && hhmm < '09:00';
}
